import { ArrowLeft, Download, Star } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { toastApiError } from "@/api/errors";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ErrorState } from "@/components/feedback/ErrorState";
import { LoadingState } from "@/components/feedback/LoadingState";
import { Separator } from "@/components/ui/separator";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  useInstallAgent,
  useMarketplaceAgent,
  useUninstallAgent,
} from "@/features/marketplace/hooks/useMarketplace";

export function AgentPublicDetailPage() {
  const { t } = useTranslation("marketplace");
  const { t: tc } = useTranslation("common");
  const { agentId = "" } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const query = useMarketplaceAgent(agentId);
  const install = useInstallAgent(agentId);
  const uninstall = useUninstallAgent(agentId);

  if (query.isLoading) return <LoadingState />;
  if (query.isError) return <ErrorState error={query.error} />;
  if (!query.data) return null;
  const a = query.data;

  async function handleInstall() {
    try {
      const result = await install.mutateAsync();
      if (result.installed) {
        toast.success(t("messages.installSuccess"));
      } else {
        toast.success(t("messages.installRequestSent"));
      }
    } catch (err) {
      toastApiError(err);
    }
  }

  async function handleUninstall() {
    try {
      await uninstall.mutateAsync();
      toast.success(t("messages.uninstallSuccess"));
    } catch (err) {
      toastApiError(err);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start gap-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/marketplace")}
          aria-label={tc("actions.back")}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <PageHeader
          eyebrow={t("title")}
          title={a.name}
          description={a.description}
          className="flex-1"
          actions={
            a.is_installed ? (
              <>
                <Button
                  onClick={() =>
                    navigate(`/threads/new?agent_id=${a.id}`)
                  }
                >
                  {t("openChat")}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleUninstall}
                  disabled={uninstall.isPending}
                >
                  {t("uninstall")}
                </Button>
              </>
            ) : (
              <Button onClick={handleInstall} disabled={install.isPending}>
                <Download className="h-4 w-4" />
                {a.requires_install_approval
                  ? t("requestInstall")
                  : t("install")}
              </Button>
            )
          }
        />
      </div>

      <div className="flex flex-wrap items-center gap-4 text-sm text-ink-500">
        {a.rating_avg !== null ? (
          <span className="tabular inline-flex items-center gap-1">
            <Star className="h-3 w-3 fill-accent-500 text-accent-500" />
            {a.rating_avg.toFixed(1)} ({a.rating_count})
          </span>
        ) : null}
        <span>{t("installs", { count: a.install_count })}</span>
        <Badge variant="outline">
          {(() => {
            switch (a.visibility) {
              case "company":
                return t("visibility.company");
              case "team_restricted":
                return t("visibility.teamRestricted");
              case "private":
                return t("visibility.private");
              default:
                return a.visibility;
            }
          })()}
        </Badge>
      </div>
      <Separator />

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">{t("tabs.overview")}</TabsTrigger>
          <TabsTrigger value="knowledge">{t("tabs.knowledge")}</TabsTrigger>
          <TabsTrigger value="tools">{t("tabs.tools")}</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="space-y-2 py-3">
          <p className="text-sm text-ink-900">{a.description}</p>
        </TabsContent>
        <TabsContent value="knowledge" className="space-y-2 py-3">
          <h2 className="text-sm font-semibold text-ink-950">
            {t("kbs.title")}
          </h2>
          {a.kbs.length === 0 ? (
            <p className="text-sm text-ink-500">{t("kbs.empty")}</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {a.kbs.map((kb) => (
                <li
                  key={kb.id}
                  className="rounded-lg border border-line bg-surface px-3 py-2 text-ink-900"
                >
                  {kb.name}
                </li>
              ))}
            </ul>
          )}
        </TabsContent>
        <TabsContent value="tools" className="space-y-2 py-3">
          <h2 className="text-sm font-semibold text-ink-950">
            {t("mcps.title")}
          </h2>
          {a.mcps.length === 0 ? (
            <p className="text-sm text-ink-500">{t("mcps.empty")}</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {a.mcps.map((mcp) => (
                <li
                  key={mcp.id}
                  className="rounded-lg border border-line bg-surface px-3 py-2"
                >
                  <div className="font-medium text-ink-950">{mcp.name}</div>
                  {mcp.enabled_tools.length > 0 ? (
                    <div className="text-xs text-ink-500">
                      {mcp.enabled_tools.join(", ")}
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
